import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import { FaArrowLeft, FaDownload } from "react-icons/fa";
import { getClientBill } from "../../../store/slices/clientBillSlice";
import styles from "./ClientBillPdf.module.scss";

const formatDate = (date) => {
  if (!date) return "";
  const parsedDate = new Date(date);
  return Number.isNaN(parsedDate.getTime())
    ? ""
    : parsedDate.toLocaleDateString("en-GB");
};

const getFilter = () => {
  try {
    return JSON.parse(sessionStorage.getItem("clientBillPdfFilter")) || {};
  } catch {
    return {};
  }
};

const ClientBillPdf = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const componentRef = useRef(null);
  const { clinicInfo } = useSelector((state) => state.clinic);
  const { token } = useSelector((state) => state.auth);
  const [bills, setBills] = useState(null);
  const [pdfHeader, setPdfHeader] = useState(null);
  const [isoLogo, setIsoLogo] = useState(null);
  const filter = useMemo(() => getFilter(), []);
  const clinic = clinicInfo?.[0] || {};
  const toBase64 = async (url) => {
    if (!url) return null;

    try {
      const separator = url.includes("?") ? "&" : "?";
      const response = await fetch(`${url}${separator}t=${Date.now()}`, {
        mode: "cors",
        cache: "no-store",
      });

      if (!response.ok) {
        throw new Error(`Image request failed: ${response.status}`);
      }

      const blob = await response.blob();

      return new Promise((resolve) => {
        const reader = new FileReader();

        reader.onloadend = () => {
          resolve(reader.result);
        };

        reader.onerror = () => {
          resolve(null);
        };

        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error("Failed to convert image to base64:", error);

      return null;
    }
  };
  useEffect(() => {
    let mounted = true;

    const loadHeaderImages = async () => {
      const headerUrl = clinic?.hospitalInfo?.pdfHeader;
      const isoUrl = clinic?.hospitalInfo?.ISOLogo;
      const [headerImage, isoImage] = await Promise.all([
        toBase64(headerUrl),
        toBase64(isoUrl),
      ]);

      if (!mounted) return;

      setPdfHeader(headerImage);
      setIsoLogo(isoImage);
    };

    loadHeaderImages();

    return () => {
      mounted = false;
    };
  }, [clinic?.hospitalInfo?.pdfHeader, clinic?.hospitalInfo?.ISOLogo]);
  useEffect(() => {
    if (!token || !filter.clientId || !clinic.clinicId) {
      setBills([]);
      return;
    }

    dispatch(
      getClientBill({
        clinicId: clinic.clinicId,
        token,
        fromDate: filter.fromDate,
        toDate: filter.toDate,
        clientId: filter.clientId,
        status: "ALL",
        getAll: true,
      }),
    )
      .then((action) => {
        const list = action?.payload?.result?.list;

        setBills(Array.isArray(list) ? list : []);
      })
      .catch((error) => {
        console.error("Failed to fetch client bills:", error);

        setBills([]);
      });
  }, [clinic.clinicId, dispatch, filter, token]);

  const handlePrint = useReactToPrint({
    contentRef: componentRef,
    documentTitle: `Client_Bill_Report_${filter.clientName || "client"}`,
    pageStyle: `
      @page {
        size: A4 portrait;
        margin: 0;
      }

      @media print {

        html,
        body {
          margin: 0 !important;
          padding: 0 !important;
          background: #ffffff !important;

          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }

        .no-print {
          display: none !important;
        }
      }
    `,
  });

  const rows = useMemo(
    () =>
      (bills || []).map((item) => {
        const appointment = item?.appointmentId || {};
        const patientName = item?.patientId?.userId?.name || "-";
        const tests = (appointment?.tests || [])
          .map((test) => test?.testId?.testName || test?.testName || "")
          .filter(Boolean);
        const profiles = (appointment?.profiles || [])
          .map(
            (profile) =>
              profile?.profileId?.profileName || profile?.profileName || "",
          )
          .filter(Boolean);
        const diagnosis = [...tests, ...profiles].join(", ");
        const totalAmount = Number(appointment?.totalAmount) || 0;
        const discount = 0;
        const receivedAmount = totalAmount;
        const paidAmount = item?.paymentStatus === "PAID" ? totalAmount : null;

        return {
          date: formatDate(appointment?.appDate),
          name: patientName,
          apptId: appointment?.appointmentId || appointment?._id || "-",
          total: totalAmount,
          discount,
          status: item?.paymentStatus || "-",
          receivedAmount,
          paidAmount,
          diagnosis: diagnosis || "-",
        };
      }),
    [bills],
  );

  const grandTotal = rows.reduce((sum, row) => sum + Number(row.total || 0), 0);
  const totalDiscount = 0;
  const paidAmount = rows.reduce(
    (sum, row) =>
      sum + (row.paidAmount === null ? 0 : Number(row.paidAmount || 0)),
    0,
  );

  const netTotal = grandTotal;
  const pendingAmount = Math.max(grandTotal - paidAmount, 0);

  return (
    <div className={styles.wrapper}>
      <div className={`${styles.actionBar} no-print`}>
        <button
          className="btn btn-sm btn-primary px-3 py-2 d-flex align-items-center gap-2"
          onClick={() => navigate(-1)}
        >
          <FaArrowLeft />
          <span>Goto Client Bill</span>
        </button>
        <button
          className="btn btn-sm btn-primary px-3 py-2 d-flex align-items-center gap-2"
          onClick={handlePrint}
        >
          <FaArrowLeft />
          <span>Download Client Bill</span>
        </button>
      </div>
      <div ref={componentRef} className={styles.reportPage}>
        <header className={styles.header}>
          <div className={styles.headerLeft}>
            {pdfHeader && (
              <img src={pdfHeader} alt="Clinic" className={styles.clinicLogo} />
            )}
            <div className={styles.addressText}>
              <strong>{clinic.clinicName || "Clinic"}</strong>
              <br />
              {clinic.clinicAddress?.plot || ""}
              {clinic.clinicAddress?.city?.name
                ? `, ${clinic.clinicAddress.city.name}`
                : ""}
              <br />
              Contact No : {clinic.phone || "+91-XXXXXXXXXX"}
            </div>
          </div>
          <div className={styles.titleBlock}>
            {isoLogo && (
              <img src={isoLogo} alt="ISO" className={styles.isoLogo} />
            )}
          </div>
        </header>

        <section className={styles.meta}>
          <table>
            <tbody>
              <tr>
                <td>
                  <strong>Client Name</strong>
                </td>
                <td>: {filter.clientName || "-"}</td>
                <td>
                  <strong>Report Date</strong>
                </td>
                <td>: {formatDate(new Date())}</td>
              </tr>
              <tr>
                <td>
                  <strong>From Date</strong>
                </td>
                <td>: {formatDate(filter.fromDate) || "-"}</td>
                <td>
                  <strong>To Date</strong>
                </td>
                <td>: {formatDate(filter.toDate) || "-"}</td>
              </tr>
            </tbody>
          </table>
        </section>

        {rows.length > 0 ? (
          <table className={styles.billTable}>
            <thead>
              <tr>
                <th>Date</th>
                <th>Appt ID</th>
                <th>Name</th>
                <th>Diagnosis</th>
                <th>Total</th>
                <th>(-) Disc</th>
                <th>Status</th>
                <th>Rec. Amt</th>
                <th>Paid Amt</th>
              </tr>
            </thead>

            <tbody>
              {rows.map((row, index) => (
                <tr key={`${row.apptId}-${index}`}>
                  <td>{row.date || "-"}</td>
                  <td>{row.apptId || "-"}</td>
                  <td>{row.name || "-"}</td>
                  <td>{row.diagnosis || "-"}</td>
                  <td className={styles.amount}>{row.total.toFixed(2)}</td>
                  <td className={styles.amount}>{row.discount.toFixed(2)}</td>
                  <td>{row.status || "-"}</td>
                  <td className={styles.amount}>
                    {row.receivedAmount.toFixed(2)}
                  </td>
                  <td className={styles.amount}>
                    {row.paidAmount === null
                      ? "UNPAID"
                      : row.paidAmount.toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className={styles.empty}>
            No records found for the selected client and date range.
          </div>
        )}

        <section className={styles.summarySection}>
          <div className={styles.amountSummary}>
            <div>
              <span>Total Amount:</span>
              <strong>₹ {grandTotal.toFixed(2)}</strong>
            </div>
            <div>
              <span>Discount:</span>
              <strong>₹ {totalDiscount.toFixed(2)}</strong>
            </div>
            <div>
              <span>Net Amount:</span>
              <strong>₹ {netTotal.toFixed(2)}</strong>
            </div>
            <div>
              <span>Paid Amount:</span>
              <strong>₹ {paidAmount.toFixed(2)}</strong>
            </div>
            <div className={styles.pendingRow}>
              <span>Pending Amount:</span>
              <strong>₹ {pendingAmount.toFixed(2)}</strong>
            </div>
          </div>
        </section>
        <footer className={styles.pageFooter}>
          <span>Powered By : SimpleeKare, Bhubaneswar.</span>
          <span>Call : (+91) 9776414444 / 6371966229</span>
          <span>www.SimpleeKare.com</span>
        </footer>
      </div>
    </div>
  );
};

export default ClientBillPdf;
